import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { fetchOverlayData, fetchGeoJsonData, fetchFaultData, fetchLatestDate, fetchBlockOutlineData, auth, fetchRepairDates, fetchPanelsForRepairDate, addPanelToRepairQueue, deletePanelFromRepairQueue } from "../utils/firebase";
import "./styleSheets/MapPage.css";
import { parseLocationString } from '../utils/helperFunctions'

//icons
import MapIcon from "@mui/icons-material/Map";
import SatelliteIcon from "@mui/icons-material/Satellite";
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import FilterIcon from '@mui/icons-material/Filter';
import { IconButton, Box, Modal, Typography, Checkbox, Slider, FormControlLabel, Button } from "@mui/material";
import HandymanIcon from '@mui/icons-material/Handyman';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Snackbar from '@mui/material/Snackbar';
import DeleteIcon from "@mui/icons-material/Delete";
import CropFreeIcon from '@mui/icons-material/CropFree';
import GetAppIcon from '@mui/icons-material/GetApp';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import * as turf from "@turf/turf";
import TextField from '@mui/material/TextField';


import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const db = getFirestore();

const MapPage = () => {
    const { siteName } = useParams();
    const mapContainer = useRef(null);
    const [loading, setLoading] = useState(true);
    const map = useRef(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [mapVisibility, setMapVisibility] = useState(false);
    const [centerCoordinates, setCenterCoordinates] = useState(null);
    const [geoJsons, setGeoJsons] = useState(null);
    const [blockOutline, setBlockOutline] = useState(null);
    const [faultData, setFaultData] = useState(null);
    const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/light-v11");
    const [overlays, setOverlays] = useState(null);
    const [overlayOpacityState, setOverlayOpacityState] = useState({});
    const [overlayVisibility, setOverlayVisibility] = useState({});
    const [filterMenuOpen, setFilterMenuOpen] = useState(false);

    const [polygonFilterOpen, setPolygonFilterOpen] = useState(false);
    const [polygonOpacity, setPolygonOpacity] = useState(0.9);

    const [faultCategories, setFaultCategories] = useState([]);
    const [faultVisibility, setFaultVisibility] = useState({});
    const [selectedFault, setSelectedFault] = useState(null);
    const [faultModalOpen, setFaultModalOpen] = useState(false);

    const [affectedPanelsCount, setAffectedPanelsCount] = useState(0);
    const [faultCategoryCounts, setFaultCategoryCounts] = useState(null);

    const lastHoveredPolygonRef = useRef(null);

    //Stuff about the repair list

    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [tempDate, setTempDate] = useState(dayjs());
    const [repairDates, setRepairDates] = useState([]);
    const [selectedRepairDate, setSelectedRepairDate] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [expandedDate, setExpandedDate] = useState(null);
    const [repairQueue, setRepairQueue] = useState({});

    const [currentRepairPanels, setCurrentRepairPanels] = useState([]);

    const [repairListModalOpen, setRepairListModalOpen] = useState(false);
    const [modalRepairPanels, setModalRepairPanels] = useState([]);
    const [modalRepairDate, setModalRepairDate] = useState(null);
    const [hoveredDateId, setHoveredDateId] = useState(null);
    const [repairQueueDirty, setRepairQueueDirty] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [panelToDelete, setPanelToDelete] = useState(null);
    const [selectAreaMode, setSelectAreaMode] = useState(false);

    const [tutorialFaded, setTutorialFaded] = useState(false);
    const [tutorialVisible, setTutorialVisible] = useState(false);
    const fadeTimerRef = useRef(null);

    const [selectionCoords, setSelectionCoords] = useState([]);


    //Stuff about the repair list ^^^


    const [kWPerPanel, setKWPerPanel] = useState(0.5);

    const fetchData = async () => {
        try {
            setLoading(true);
            console.log("fetching site data");
            const latestDate = await fetchLatestDate(siteName);
            setSelectedDate(latestDate);

            const [blockData, allGeoJsons, faults, overlayData] = await Promise.all([
                fetchBlockOutlineData(siteName),
                fetchGeoJsonData(siteName),
                fetchFaultData(siteName, latestDate),
                fetchOverlayData(siteName),
            ]);

            console.log("Block Outline Data Retrieved:", blockData);
            console.log("All GeoJSON Data Retrieved:", allGeoJsons);

            setBlockOutline(blockData);
            setGeoJsons(allGeoJsons || {});
            setFaultData(faults);
            setOverlays(overlayData);

            if (allGeoJsons && Object.keys(allGeoJsons).length > 0) {
                const lastIB = Object.keys(allGeoJsons).pop(); // Get last IB key
                const lastIBGeoJson = allGeoJsons[lastIB];

                if (lastIBGeoJson && lastIBGeoJson.features && lastIBGeoJson.features.length > 0) {
                    const lastFeature = lastIBGeoJson.features[lastIBGeoJson.features.length - 1];
                    if (lastFeature.geometry && lastFeature.geometry.coordinates.length > 0) {
                        const lastPolygon = lastFeature.geometry.coordinates[0];
                        if (lastPolygon.length > 0) {
                            const lastPoint = lastPolygon[lastPolygon.length - 1];
                            setCenterCoordinates([lastPoint[0], lastPoint[1]]);
                            console.log(`Setting map center to: ${lastPoint[1]}, ${lastPoint[0]}`);
                        }
                    }
                }
            } else {
                console.warn("Warning: No IB GeoJSON data found.");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching initial site data: ", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [siteName]);

    useEffect(() => {
        if (!siteName) return;

        const siteDocRef = doc(db, "sites", siteName);

        const unsubscribe = onSnapshot(siteDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                setKWPerPanel(data.customFields?.panelProd || 0.5);
            }
        });

        return () => unsubscribe();
    }, [siteName]);

    useEffect(() => {
        if (!selectedDate) return;

        const fetchFaultsForDate = async () => {
            try {
                console.log(`Fetching fault data for date: ${selectedDate}`);
                const faults = await fetchFaultData(siteName, selectedDate);
                setFaultData(faults);
            } catch (error) {
                console.error("Error fetching fault data: ", error);
            }
        };
        fetchFaultsForDate();
    }, [selectedDate, siteName]);

    const toggleMapStyle = () => {
        const newStyle = mapStyle === "mapbox://styles/mapbox/light-v11"
            ? "mapbox://styles/mapbox/satellite-v9"
            : "mapbox://styles/mapbox/light-v11";

        setMapStyle(newStyle);

        if (map.current) {
            map.current.setStyle(newStyle);
            console.log(`Map style changed to: ${newStyle.includes("satellite") ? "Satellite" : "Light"}`);

            map.current.once("style.load", () => {
                loadOverlays();
            });

            map.current.once("style.load", ()=> {
                window.hvtMap = map.current;
                loadOverlays();
            });

            setTimeout(() => {
                const currentZoom = map.current.getZoom();
                map.current.zoomTo(currentZoom + 0.3, { duration: 1000 });
            }, 1000);

            setTimeout(() => {
                const currentZoom = map.current.getZoom();
                map.current.zoomTo(currentZoom - 0.3, { duration: 1000 });
            }, 1150);

        }
    };

    const toggleOverlayVisibility = (category) => {
        setOverlayVisibility((prev) => {
            const newVisibility = { ...prev, [category]: !prev[category] };
            const visibility = newVisibility[category] ? "visible" : "none";

            if (map.current.getLayer(`${category}-overlay`)) {
                map.current.setLayoutProperty(`${category}-overlay`, "visibility", visibility);
                console.log(`Toggled visibility of ${category}-overlay to ${visibility}`);
            } else {
                console.warn(`Layer ${category}-overlay not found.`);
            }

            return newVisibility;
        });
    };

    const handleOpacityChange = (category, value) => {
        setOverlayOpacityState((prev) => ({
            ...prev,
            [category]: value,
        }));

        if (map.current.getLayer(`${category}-overlay`)) {
            map.current.setPaintProperty(`${category}-overlay`, "raster-opacity", value);
            console.log(`Set opacity for ${category}-overlay to ${value}`);
        } else {
            console.warn(`Layer ${category}-overlay not found.`);
        }
    };

    const toggleFilterMenu = () => {
        setFilterMenuOpen(!filterMenuOpen);
    };

    // Initialize Map
    useEffect(() => {
        if (!map.current && centerCoordinates) {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: mapStyle,
                center: centerCoordinates,
                zoom: 17,
                preserveDrawingBuffer: true,
            });

            map.current.on("load", () => {
                console.log("Map Loaded");
                setPolygonOpacity(1);
                console.log("Polygon opacity set to 1 as well ");
                window.hvtMap = map.current;
            });
        }
    }, [centerCoordinates]);

    useEffect(() => {
        if (map.current && overlays) {
            console.log("Updating overlays and syncing checkboxes.");
            const initialVisibility = {};

            Object.keys(overlays).forEach((category) => {
                initialVisibility[category] = false;
            });

            setOverlayVisibility(initialVisibility);
        }
    }, [overlays]);

    useEffect(() => {
        const fetchOverlays = async () => {
            try {
                const overlayData = await fetchOverlayData(siteName);
                console.log("Overlay data fetched:", overlayData);
                if (overlayData) {
                    setOverlays(overlayData);

                    const initialVisibility = Object.keys(overlayData).reduce((acc, category) => {
                        acc[category] = false;
                        return acc;
                    }, {});
                    setOverlayVisibility(initialVisibility);
                }
            } catch (error) {
                console.error("Error fetching overlays in MapPage:", error);
            }
        };

        fetchOverlays();
    }, [siteName]);

    useEffect(() => {
        if (!map.current || !overlays) return;

        console.log("Overlays fetched—loading them onto the map.");
        if (!map.current.isStyleLoaded()) {
            map.current.once("style.load", () => {
                console.log("Map style has loaded, adding overlays...");
                loadOverlays();
            });
        } else {
            loadOverlays();
        }
    }, [overlays]);

    const blockColorsRef = useRef({});

    useEffect(() => {
        if (!map.current || !blockOutline || !faultData) return;

        const layerId = "block-outline-layer";
        const labelLayerId = "block-label-layer";

        console.log("Adding block outlines with predefined colors and affected panel counts...");

        const colorPalette = [
            "#FF5F24", "#5851FD", "#235A0E", "#E9556D", "#C2A328",
            "#1C959A", "#9A7743", "#8E86FA", "#5F8F6A", "#6F2A25",
            "#588753", "#DCB175", "#384657"
        ];

        const blockColors = {};
        blockOutline.features.forEach((feature, index) => {
            const blockNumber = feature.properties.block;
            blockColors[blockNumber] = colorPalette[index % colorPalette.length];
        });

        const affectedPanelsPerBlock = {};

        Object.values(faultData).forEach((fault) => {
            const match = fault.location.match(/^IB(\d+)/);
            if (match) {
                const blockNumber = match[1];
                if (!affectedPanelsPerBlock[blockNumber]) {
                    affectedPanelsPerBlock[blockNumber] = 0;
                }
                affectedPanelsPerBlock[blockNumber] += 1;
            }
        });

        const addBlockOutlines = () => {
            if (!map.current.isStyleLoaded()) {
                console.warn("Map style is not fully loaded yet. Retrying...");
                setTimeout(addBlockOutlines, 500);
                return;
            }

            console.log("Map style loaded. Adding block outlines...");

            if (!map.current.getSource(layerId)) {
                map.current.addSource(layerId, {
                    type: "geojson",
                    data: blockOutline,
                });

                const fillColorExpression = ["match", ["get", "block"]];
                Object.entries(blockColors).forEach(([block, color]) => {
                    fillColorExpression.push(parseInt(block, 10), color);
                });
                fillColorExpression.push("#000");

                map.current.addLayer({
                    id: layerId,
                    type: "fill",
                    source: layerId,
                    paint: {
                        "fill-color": fillColorExpression,
                        "fill-opacity": 0.6,
                        "fill-outline-color": "#000",
                    },
                });

                map.current.addLayer({
                    id: labelLayerId,
                    type: "symbol",
                    source: layerId,
                    layout: {
                        "text-field": [
                            "concat",
                            "Block ", ["get", "block"],
                            "\n",
                            ["concat", ["to-string", ["coalesce", ["get", "affected_panels"], 0]], " affected"]
                        ],
                        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                        "text-size": 14,
                        "text-anchor": "center",
                    },
                    paint: {
                        "text-color": "#000",
                        "text-halo-color": "#fff",
                        "text-halo-width": 1.5,
                    },
                });

                console.log("Block outlines and labels with affected panel counts added.");
            }

            map.current.getSource(layerId).setData({
                ...blockOutline,
                features: blockOutline.features.map(feature => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        affected_panels: affectedPanelsPerBlock[feature.properties.block] || 0
                    }
                }))
            });
        };

        if (map.current.isStyleLoaded()) {
            addBlockOutlines();
        } else {
            console.log("Waiting for Mapbox style to load...");
            map.current.once("style.load", addBlockOutlines);
        }

        map.current.on("style.load", addBlockOutlines);

        return () => {
            map.current.off("style.load", addBlockOutlines);
        };
    }, [blockOutline, faultData, mapStyle]);


    useEffect(() => {
        if (!map.current) return;

        const updateVisibility = () => {
            if (!map.current.isStyleLoaded()) return;

            const zoom = map.current.getZoom();
            const maxOpacity = polygonOpacity;
            console.log(`🔍 Current Zoom Level: ${zoom}`);

            if (map.current.getLayer("block-outline-layer") && map.current.getLayer("ib-polygon-layer")) {
                if (zoom < 17) {

                    map.current.setPaintProperty("block-outline-layer", "fill-opacity", maxOpacity);
                    map.current.setPaintProperty("ib-polygon-layer", "fill-opacity", 0);
                } else if (zoom >= 17.5) {
                    map.current.setPaintProperty("block-outline-layer", "fill-opacity", Math.min(0.1, maxOpacity));
                    map.current.setPaintProperty("ib-polygon-layer", "fill-opacity", Math.min(0.9, maxOpacity));
                }
            } else {
                console.warn("Waiting for layers to load before setting opacity...");
                setTimeout(updateVisibility, 500);
            }
        };

        if (map.current.isStyleLoaded()) {
            console.log("Map style loaded—running updateVisibility()");
            updateVisibility();
        } else {
            console.log("Waiting for map style to load...");
            map.current.once("style.load", () => {
                console.log("Style load event triggered—running updateVisibility()");
                updateVisibility();
            });
        }

        map.current.on("zoom", updateVisibility);

        return () => {
            map.current.off("zoom", updateVisibility);
        };
    }, [polygonOpacity]);


    useEffect(() => {
        if (!map.current || !geoJsons || !faultData) return;

        const ibLayerId = "ib-polygon-layer";
        const maxOpacity = polygonOpacity;

        console.log("Adding IB polygons to the map...");

        const addIBPolygons = () => {
            if (!map.current.isStyleLoaded()) {
                console.warn("Map style is not fully loaded yet. Retrying...");
                setTimeout(addIBPolygons, 500);
                return;
            }

            console.log("Map style loaded. Adding IB polygons...");

            if (!map.current.getSource(ibLayerId)) {
                map.current.addSource(ibLayerId, {
                    type: "geojson",
                    data: { type: "FeatureCollection", features: Object.values(geoJsons).flatMap(ib => ib.features) },
                });

                const faultDataMap = {};
                Object.values(faultData).forEach((fault) => {
                    faultDataMap[fault.location] = fault.level;
                });

                const getFaultColor = (faultLevel) => {
                    switch (faultLevel) {
                        case "Level 1": return "#E0B000";
                        case "Level 2": return "#E05600";
                        case "Level 3": return "#E00000";
                        default: return "#808080";
                    }
                };

                const colorExpression = ["match", ["get", "location"],
                    ...Object.entries(faultDataMap).flatMap(([location, level]) => [location, getFaultColor(level)]),
                    "#808080"
                ];

                map.current.addLayer({
                    id: ibLayerId,
                    type: "fill",
                    source: ibLayerId,
                    paint: {
                        "fill-color": colorExpression,
                        "fill-opacity": 0,
                        "fill-outline-color": "#000",
                    },
                });

                console.log("IB polygons added with initial opacity: 0");

                map.current.on("click", ibLayerId, (e) => {
                    if (!e.features || e.features.length === 0) return;

                    const clickedFeature = e.features[0];
                    const clickedLocation = clickedFeature.properties.location;
                    console.log("Polygon Clicked:", clickedLocation);

                    const fault = Object.values(faultData).find(f => f.location === clickedLocation);
                    let latitude = "N/A";
                    let longitude = "N/A";

                    if (!fault) {
                        const coordinates = clickedFeature.geometry.coordinates[0];
                        let sumLat = 0, sumLng = 0, numPoints = coordinates.length;

                        coordinates.forEach(([lng, lat]) => {
                            sumLat += lat;
                            sumLng += lng;
                        });

                        latitude = (sumLat / numPoints).toFixed(6);
                        longitude = (sumLng / numPoints).toFixed(6);
                    } else {
                        latitude = fault.latitude;
                        longitude = fault.longitude;
                    }

                    setSelectedFault({
                        location: clickedLocation,
                        level: fault?.level || "N/A",
                        category: fault?.category || "Panel in working order",
                        latitude: latitude,
                        longitude: longitude,
                        notes: fault?.notes || "No additional notes",
                        images: fault?.images || [],
                    });

                    setFaultModalOpen(true);
                });

            }
        };

        if (map.current.isStyleLoaded()) {
            addIBPolygons();
        } else {
            console.log("Waiting for Mapbox style to load...");
            map.current.once("style.load", addIBPolygons);
        }

        map.current.on("style.load", addIBPolygons);

        return () => {
            map.current.off("click", ibLayerId);
        };
    }, [geoJsons, faultData, mapStyle]);

    useEffect(() => {
        if (map.current && map.current.getLayer("ib-polygon-layer")) {
            map.current.setPaintProperty("ib-polygon-layer", "fill-opacity", polygonOpacity);
        }
        if (map.current && map.current.getLayer("ib-label-layer")) {
            map.current.setPaintProperty("ib-label-layer", "text-opacity", polygonOpacity);
        }
    }, [polygonOpacity]);

    useEffect(() => {
        if (!faultData) return;

        const categories = [...new Set(Object.values(faultData).map(fault => fault.category))];

        console.log("Extracted Fault Categories:", categories);
        setFaultCategories(categories);
        const initialVisibility = categories.reduce((acc, category) => {
            acc[category] = true;
            return acc;
        }, {});
        setFaultVisibility(initialVisibility);

    }, [faultData]);

    useEffect(() => {
        if (!faultData || Object.keys(faultData).length === 0) {
            console.warn("No fault data available yet. Resetting counters.");
            setAffectedPanelsCount(0);
            setFaultCategoryCounts({});
            return;
        }

        console.log("Recalculating affected panels...");
        console.log("Fault Data:", faultData);

        // Count total faults (affected panels)
        const totalAffectedPanels = Object.keys(faultData).length;

        // Count faults per category
        const categoryCounts = {};
        Object.values(faultData).forEach((fault) => {
            const category = fault.category || "Unknown"; // Handle missing category
            categoryCounts[category] = (categoryCounts[category] || 0) + 1;
        });

        console.log("✅ Updated Fault Category Breakdown:", categoryCounts);
        setAffectedPanelsCount(totalAffectedPanels);
        setFaultCategoryCounts(categoryCounts);
    }, [faultData]);


    const getFaultColor = (faultLevel, category) => {
        if (!faultVisibility[category]) return "#808080";
        switch (faultLevel) {
            case "Level 1": return "#E0B000";
            case "Level 2": return "#E05600";
            case "Level 3": return "#E00000";
            default: return "#808080";
        }
    };

    const updatePolygonColors = () => {
        if (!map.current || !faultData) return;

        if (!map.current.getLayer("ib-polygon-layer")) {
            console.warn("Layer not found: ib-polygon-layer");
            return;
        }

        const colorExpression = ["match", ["get", "location"]];

        Object.values(faultData).forEach(fault => {
            const isVisible = faultVisibility[fault.category] ?? true;
            const color = isVisible ? getFaultColor(fault.level, fault.category) : "rgba(0,0,0,0)"; // 🔹 Hide fully instead of gray

            colorExpression.push(fault.location, color);
        });

        colorExpression.push("rgba(0,0,0,0)"); // Ensure hidden faults don't appear

        map.current.setPaintProperty("ib-polygon-layer", "fill-color", colorExpression);
        console.log("Polygon colors updated!");
    };


    const toggleFaultVisibility = (category) => {
        setFaultVisibility((prev) => {
            const newState = { ...prev, [category]: !prev[category] };
            return newState;
        });
    };

    useEffect(() => {
        updatePolygonColors();
    }, [faultVisibility]);


    const loadOverlays = () => {
        if (!overlays || !map.current) {
            console.warn("No overlays to load.");
            return;
        }

        Object.entries(overlays).forEach(([category, overlayData]) => {
            const { image, bounds } = overlayData;
            const layerId = `${category}-overlay`;

            console.log(`Loading image for category: ${category}`);

            bounds.text()
                .then((jgwContent) => {
                    console.log(`JGW data for [${category}]: ${jgwContent}`);
                    const img = new Image();
                    img.src = URL.createObjectURL(image);
                    img.onload = () => {
                        const imageOverlay = { width: img.width, height: img.height };
                        const parsedBounds = parseJGW(jgwContent, imageOverlay);

                        if (!parsedBounds) {
                            console.error(`Failed to parse bounds for category: ${category}`);
                            return;
                        }

                        if (map.current) {
                            if (!map.current.getSource(layerId)) {
                                map.current.addSource(layerId, {
                                    type: "image",
                                    url: img.src,
                                    coordinates: parsedBounds,
                                });

                                const layers = map.current.getStyle().layers;
                                let beforeLayer = null;

                                for (let i = 0; i < layers.length; i++) {
                                    if (layers[i].id.includes("geojson-layer")) {
                                        beforeLayer = layers[i].id;
                                        break;
                                    }
                                }

                                if (!map.current.getLayer(layerId)) {
                                    map.current.addLayer({
                                        id: layerId,
                                        type: "raster",
                                        source: layerId,
                                        layout: {
                                            "visibility": overlayVisibility[category] ? "visible" : "none",
                                        },
                                        paint: {
                                            "raster-opacity": overlayOpacityState[category] || 1,
                                        },
                                    }, beforeLayer);
                                }
                            } else {
                                console.log(`Layer ${layerId} already exists, updating opacity...`);
                                map.current.setPaintProperty(layerId, "raster-opacity", overlayOpacityState[category] || 1);
                            }
                        }
                    };
                })
                .catch((error) => {
                    console.error(`Error parsing .jgw file for category: ${category}, error`);
                });
        });
    };

    const parseJGW = (content, imageOverlay) => {
        const lines = content.trim().split("\n");
        if (lines.length !== 6) {
            console.error("Invalid .jgw file format.");
            return null;
        }

        const pixelSizeX = parseFloat(lines[0]);
        const pixelSizeY = parseFloat(lines[3]);
        const upperLeftX = parseFloat(lines[4]);
        const upperLeftY = parseFloat(lines[5]);

        if (isNaN(pixelSizeX) || isNaN(pixelSizeY) || isNaN(upperLeftX) || isNaN(upperLeftY)) {
            console.error("Invalid .jgw file contents.");
            return null;
        }

        const imageWidth = imageOverlay.width;
        const imageHeight = imageOverlay.height;

        const lowerRightX = upperLeftX + pixelSizeX * imageWidth;
        const lowerRightY = upperLeftY + pixelSizeY * imageHeight;

        // Convert EPSG:3857 to EPSG:4326
        const convertToEPSG4326 = (x, y) => {
            const R = 6378137;
            const lon = (x / R) * (180 / Math.PI);
            const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);
            return [parseFloat(lon.toFixed(12)), parseFloat(lat.toFixed(12))];
        };

        const upperLeft = convertToEPSG4326(upperLeftX, upperLeftY);
        const lowerRight = convertToEPSG4326(lowerRightX, lowerRightY);

        return [
            [upperLeft[0], upperLeft[1]],
            [lowerRight[0], upperLeft[1]],
            [lowerRight[0], lowerRight[1]],
            [upperLeft[0], lowerRight[1]],
        ];
    };
    const handleCloseFaultModal = useCallback(() => {
        setFaultModalOpen(false);
        setSelectedFault(null);
    }, []);


    useEffect(() => {
        console.log(" HeaderBar Mounted");
        return () => console.log("HeaderBar Unmounted");
    }, []);


//REPAIR DATE FUNCTIONS 
    useEffect(() => {
        const fetchDates = async () => {
            const dates = await fetchRepairDates(siteName);
            setRepairDates(dates);
        };
        fetchDates();
    }, [siteName]);

    useEffect(() => {
        const loadPanels = async () => {
            if (!selectedRepairDate) return;
            const panels = await fetchPanelsForRepairDate(siteName, selectedRepairDate);
            console.log(`Panels fetched for ${selectedRepairDate}:`, panels);
            setCurrentRepairPanels(panels);
        };

        loadPanels();
    }, [selectedRepairDate, siteName]);

    useEffect(() => {
        const fetchAllPanelData = async () => {
            const newQueue = {};
            for (const date of repairDates) {
                const panels = await fetchPanelsForRepairDate(siteName, date);
                newQueue[date] = panels;
            }
            setRepairQueue(newQueue);
        };

        if (repairDates.length > 0) {
            fetchAllPanelData();
        }
    }, [repairDates, siteName]);


    const handleAddPanelToRepairQueue = async (panelData) => {
        try {
            if (!selectedRepairDate) {
                setSnackbarMessage("Please select a repair date first.");
                setSnackbarOpen(true);
                return;
            }

            if (!panelData?.location) {
                setSnackbarMessage("Invalid panel data: missing location.");
                setSnackbarOpen(true);
                return;
            }

            if (isNaN(panelData.latitude) || isNaN(panelData.longitude)) {
                setSnackbarMessage("Invalid panel coordinates.");
                setSnackbarOpen(true);
                return;
            }

            const panelId = panelData.location;
            const panelEntry = {
                panelId,
                faultType: panelData.category,
                level: panelData.level,
                location: panelData.location,
                timestamp: new Date().toISOString(),
                latitude: panelData.latitude,
                longitude: panelData.longitude
            };

            await addPanelToRepairQueue(siteName, selectedRepairDate, "inspection", panelEntry);
            const updatedPanels = await fetchPanelsForRepairDate(siteName, selectedRepairDate);

            setRepairQueue(prev => ({
                ...prev,
                [selectedRepairDate]: updatedPanels
            }));

            setSnackbarMessage(`Panel ${panelId} added to repair list.`);
            setSnackbarOpen(true);

        } catch (error) {
            console.error("Error adding panel to repair list:", error);
            setSnackbarMessage("Failed to add panel to repair list.");
            setSnackbarOpen(true);
        }
    };

    const exportRepairListToCSV = () => {
        if (!modalRepairPanels || modalRepairPanels.length === 0) {
            alert("No repair panels to export.");
            return;
        }

        const headers = ["panelId", "faultType", "level", "latitude", "longitude", "location", "timestamp"];
        const csvRows = [headers.join(",")];

        modalRepairPanels.forEach(panel => {
            const row = headers.map(header => {
                let value = panel[header];

                if (typeof value === "number" && (header === "latitude" || header === "longitude")) {
                    value = value.toFixed(9); // Up to 9 decimal places
                } else if (typeof value === "object" && value?.seconds) {
                    value = new Date(value.seconds * 1000).toISOString(); // Firestore timestamp
                }
                return `"${String(value || "").replace(/"/g, '""')}"`;
            });
            csvRows.push(row.join(","));
        });

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `repair-list-${modalRepairDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const handleToggleAreaSelect = () => {
        setSelectAreaMode(prev => {
            const newMode = !prev;

            if (newMode) {
                setTutorialFaded(false);
                setTutorialVisible(false); // Reset visibility first
                if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);

                // Add "show" class after small delay to trigger fade-in
                setTimeout(() => {
                    setTutorialVisible(true);
                }, 10); // short delay is enough

                fadeTimerRef.current = setTimeout(() => {
                    setTutorialFaded(true);
                }, 5000);
            } else {
                setTutorialFaded(false);
                setTutorialVisible(false);
                if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
            }

            return newMode;
        });
    };

    //POLYGONDRAWING START

    const handleConfirmSelection = async () => {
        console.log("🧠 Available faultData keys:", Object.keys(faultData));

        if (selectionCoords.length < 3) return;

        console.log("confirmon polygon selection (1)");
        const drawnPolygon = turf.polygon([[...selectionCoords, selectionCoords[0]]]);
        const faultFeatures = Object.values(faultData).map((fault) => {
            const location = fault.location;
            const matchingFeature = Object.values(geoJsons || {}).flatMap(ib => ib.features)
                .find(f => f.properties.location === location);

            return matchingFeature || null;
        }).filter(Boolean);

        console.log("Total IB Features to check (2): ", faultFeatures.length);

        const matchedPanels = faultFeatures.filter((feature) => {
            const poly = turf.polygon(feature.geometry.coordinates);
            const within = turf.booleanWithin(poly, drawnPolygon);
            if (within) {
                console.log("Feature inside polygon: (3)", feature.properties.location);
            }
            return within;
        });

        console.log("Panels inside area: (4)", matchedPanels.length);

        const panelsToAdd = matchedPanels
            .map((f) => {
                const location = f.properties.location;

                const fault = Object.values(faultData).find(
                    (entry) => entry.location === location
                );

                if (!fault) {
                    console.warn("No fault data found for: (5)", location);
                    return null;
                }

                if (fault.category === "Panel in working order") {
                    console.log("Skipping working panel: (6)", location);
                    return null;
                }

                return {
                    ...fault,
                    panelId: fault.location,
                    latitude: fault.latitude,
                    longitude: fault.longitude,
                };
            })
            .filter(Boolean);


        console.log("Panels to be added to repair queue: 777", panelsToAdd.length);

        for (const panel of panelsToAdd) {
            console.log("Adding panel: (8)", panel.panelId);
            await handleAddPanelToRepairQueue(panel);
        }

        setSelectionCoords([]);
        setSelectAreaMode(false);
    };

    const handleCancelSelection = () => {
        setSelectionCoords([]);
        setSelectAreaMode(false);
    };


    useEffect(() => {
        if (!map.current || !selectAreaMode) return;

        const sourceId = "selection-polygon";
        const layerId = "selection-polygon-layer";

        if (!map.current.getSource(sourceId)) {
            map.current.addSource(sourceId, {
                type: "geojson",
                data: {
                    type: "FeatureCollection",
                    features: [],
                },
            });
        }

        if (!map.current.getLayer(layerId)) {
            map.current.addLayer({
                id: layerId,
                type: "fill",
                source: sourceId,
                paint: {
                    "fill-color": "#285335",
                    "fill-opacity": 0.3,
                    "fill-outline-color": "#000",
                },
            });
        }

        return () => {
            map.current.getCanvas().style.cursor = "";
            if (map.current.getLayer(layerId)) {
                map.current.removeLayer(layerId);
            }
            if (map.current.getSource(sourceId)) {
                map.current.removeSource(sourceId);
            }
        };
    }, [selectAreaMode]);


    useEffect(() => {
        if (!map.current || selectionCoords.length === 0) return;

        // -------- Update Polygon --------
        const coords = [...selectionCoords];
        if (coords.length >= 3) coords.push(coords[0]);

        const polygon = {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    geometry: {
                        type: "Polygon",
                        coordinates: [coords],
                    },
                    properties: {},
                },
            ],
        };

        map.current.getSource("selection-polygon")?.setData(polygon);

        // -------- Update Dots (Points) --------
        const pointFeatures = selectionCoords.map((coord) => ({
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: coord,
            },
            properties: {},
        }));

        const pointsGeoJson = {
            type: "FeatureCollection",
            features: pointFeatures,
        };

        // If not already added, add source + layer for dots
        if (!map.current.getSource("selection-points")) {
            map.current.addSource("selection-points", {
                type: "geojson",
                data: pointsGeoJson,
            });

            map.current.addLayer({
                id: "selection-points-layer",
                type: "circle",
                source: "selection-points",
                paint: {
                    "circle-radius": 5,
                    "circle-color": "#ffcc00",
                    "circle-stroke-color": "#000",
                    "circle-stroke-width": 1,
                },
            });
        } else {
            map.current.getSource("selection-points").setData(pointsGeoJson);
        }
    }, [selectionCoords]);


    useEffect(() => {
        if (!map.current || !selectAreaMode) return;
        map.current.getCanvas().style.cursor = "crosshair";


        const handleMapClick = (e) => {
            const { lng, lat } = e.lngLat;
            setSelectionCoords(prev => [...prev, [lng, lat]]);
        };

        map.current.on("click", handleMapClick);

        return () => {
            map.current.off("click", handleMapClick);
            setSelectionCoords([]); // reset when exiting select mode
        };
    }, [selectAreaMode]);


    useEffect(() => {
        if (!selectAreaMode && map.current) {
            const empty = {
                type: "FeatureCollection",
                features: [],
            };

            // Clear the polygon
            if (map.current.getSource("selection-polygon")) {
                map.current.getSource("selection-polygon").setData(empty);
            }

            // Clear the dots
            if (map.current.getSource("selection-points")) {
                map.current.getSource("selection-points").setData(empty);
            }

            setSelectionCoords([]);
        }
    }, [selectAreaMode]);


    //POLYGON DRAWING END ^^^^^^^^
    //POLYGON DRAWING END ^^^^^^^^
    //POLYGON DRAWING END ^^^^^^^^
    //POLYGON DRAWING END ^^^^^^^^
    //POLYGON DRAWING END ^^^^^^^^
    //POLYGON DRAWING END ^^^^^^^^


    //Loading skelton
    if (loading) return (
        <div className="map-page skeleton">
            <div className="map-container">
                <div className="map-toolbar">
                    <div className="skeleton-button" />
                    <div className="skeleton-button" />
                    <div className="skeleton-button" />
                </div>
                <div className="stats-sidebar">
                    <h3>Fault Statistics</h3>
                    <div className="skeleton-table" />
                    <div className="skeleton-stats">
                        <div className="skeleton-bar" />
                        <div className="skeleton-bar small" />
                    </div>
                </div>
            </div>
        </div>
    );

    //Full content of mapPage, excludes top bars like the nav bar and the header bar. 
    return (
        <div className="page-layout">

            <div className="map-page">
                <div className="map-container" ref={mapContainer}>

                    <div className="repair-queue-dropdown">
                        <div
                            className="dropdown-button repair-queue-button"
                            onClick={() => setDropdownOpen((prev) => !prev)}
                        >
                            {selectedRepairDate
                                ? `Repair List: ${dayjs(selectedRepairDate, "MMDDYYYY").format("MMMM D, YYYY")}`
                                : "Select Repair Date"}

                            <span className={`arrow ${dropdownOpen ? 'open' : ''}`}><HandymanIcon /></span>
                        </div>

                        <div className={`repair-queue-list ${dropdownOpen ? "open" : ""}`}>
                            <div className="repair-queue-scrollable">
                                {repairDates.map((dateId) => (
                                    <div className="repair-queue-date-wrapper" key={dateId}>
                                        <div className={`repair-queue-entry ${selectedRepairDate === dateId ? "selected" : ""}`}>
                                            <div className="left-indicator" />
                                            <div className="repair-queue-date" onClick={() => { setSelectedRepairDate(dateId); setDropdownOpen(false); }}>

                                                <span className={`date-text ${hoveredDateId === dateId ? "fade-out" : "fade-in"}`}>
                                                    {dayjs(dateId, "MMDDYYYY").format("MMMM D, YYYY")}
                                                </span>

                                                <span className={`hover-text ${hoveredDateId === dateId ? "fade-in" : "fade-out"}`}>
                                                    <span className="hover-content">
                                                        View List <ChevronRightIcon style={{ fontSize: '16px' }} />
                                                    </span>
                                                </span>
                                            </div>

                                            <button
                                                className={`expand-btn ${expandedDate === dateId ? "rotated" : ""}`}
                                                onMouseEnter={() => setHoveredDateId(dateId)}
                                                onMouseLeave={() => setHoveredDateId(null)}
                                                onClick={async () => {
                                                    if (repairQueueDirty) {
                                                        const updatedPanels = await fetchPanelsForRepairDate(siteName, dateId);
                                                        setRepairQueue(prev => ({
                                                            ...prev,
                                                            [dateId]: updatedPanels
                                                        }));
                                                        setRepairQueueDirty(false); // Clear flag after refresh
                                                    }

                                                    setModalRepairPanels(repairQueue[dateId] || []);
                                                    setModalRepairDate(dateId);
                                                    setRepairListModalOpen(true);
                                                }}

                                                title="Expand list"
                                            >
                                                <ChecklistIcon />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Add New Date */}
                            <div
                                className="repair-queue-add-button"
                                onClick={() => setDatePickerOpen(true)}
                            >
                                + Add New Date
                            </div>

                            <div
                                onClick={handleToggleAreaSelect}
                                className={`repair-queue-add-button select-area-btn ${selectAreaMode ? "active" : ""}`}
                            >
                                <span> <CropFreeIcon /></span>
                                {selectAreaMode ? "Cancel Area Select" : "Select Panels Within Area"}
                            </div>

                        </div>
                    </div>

                    {selectAreaMode && (
                        <div
                            className={`area-select-tutorial ${tutorialVisible ? "show" : ""} ${tutorialFaded ? "fade" : ""}`}
                            onMouseEnter={() => setTutorialFaded(false)}
                        >
                            <strong>Select multiple panels within an area. </strong>
                            <p>Click points on the map to select an area of affected panels to add to the repair list.</p>
                        </div>
                    )}

                    {selectAreaMode && (
                        <div className="area-select-confirm-panel">
                            <button className="confirm-btn" onClick={handleConfirmSelection}>Confirm</button>
                        </div>
                    )}



                    <div className="map-toolbar">

                        {/* map toggle */}
                        <button
                            className="map-button"
                            onClick={toggleMapStyle}
                            aria-label="Background map raster toggle"
                            title="Map Toggle"
                        >
                            {mapStyle.includes("satellite") ? <SatelliteIcon /> : <MapIcon />}
                        </button>

                        {/* overlay filter menu */}
                        <button
                            className="map-button"
                            onClick={toggleFilterMenu}
                            title="Filter Overlays"
                            aria-label="Overlay adjuster toolbar button"
                        >
                            <FilterIcon />
                        </button>

                        <button
                            className="map-button"
                            onClick={() => setPolygonFilterOpen(true)}
                            title="Polygon Filter"
                            aria-label="Polygon adjuster toolbar button"
                        >
                            <CalendarViewMonthIcon />
                        </button>

                    </div>

                    <div className="stats-sidebar">
                        <h3>Fault Statistics</h3>

                        {/* Fault Category Breakdown */}
                        <div className="stats-table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th>Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(faultCategoryCounts).map(([category, count]) => (
                                        <tr key={category}>
                                            <td>{category}</td>
                                            <td>{count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="stats-table-total">
                            <h4>Total Panels Affected</h4>
                            <a>{affectedPanelsCount}</a>
                            <h5>{(affectedPanelsCount * kWPerPanel).toFixed(2)} KW</h5>
                        </div>
                    </div>

                </div>
            </div>

            {/* MODALS MODALS MODALS MODALS MODALS*/}
            {/* MODALS MODALS MODALS MODALS MODALS*/}
            {/* MODALS MODALS MODALS MODALS MODALS*/}
            {/* MODALS MODALS MODALS MODALS MODALS*/}
            {/* MODALS MODALS MODALS MODALS MODALS*/}
            {/* MODALS MODALS MODALS MODALS MODALS*/}

            {repairListModalOpen && (
                <div
                    className="panel-repair-modal-backdrop"
                    onClick={() => setRepairListModalOpen(false)}
                >
                    <div
                        className="panel-repair-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3>Repair List: {dayjs(modalRepairDate, "MMDDYYYY").format("MMMM D, YYYY")}</h3>
                        {modalRepairPanels.length > 0 ? (
                            <ul className="panel-repair-list">
                                {modalRepairPanels.map((panel, idx) => (
                                    <li key={idx} className="panel-repair-item">

                                        <strong>
                                            {(() => {
                                                const parsed = parseLocationString(panel.location);
                                                return parsed
                                                    ? `Block ${parsed.ib}, Group ${parsed.group}, Row ${parsed.row} North, Column ${parsed.col} West`
                                                    : panel.location || `Lat: ${panel.latitude}, Lng: ${panel.longitude}`;
                                            })()}
                                        </strong>

                                        <div className="panel-meta">
                                            <p><b>Level:</b> {panel.level || "N/A"}</p>
                                            <p><b>Category:</b> {panel.faultType || "N/A"}</p>
                                            <p><b>Notes:</b> {panel.notes || "None"}</p>
                                            <p><b>Coordinates:</b>
                                                {Number(panel.latitude) ? Number(panel.latitude).toFixed(9) : "N/A"},
                                                {Number(panel.longitude) ? Number(panel.longitude).toFixed(9) : "N/A"}
                                            </p>
                                        </div>

                                        <button
                                            className="delete-panel-btn"
                                            title="Delete Panel"
                                            onClick={() => {
                                                setPanelToDelete(panel);
                                                setDeleteConfirmOpen(true);
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </button>

                                    </li>
                                ))}
                            </ul>

                        ) : (
                            <em>No panels in this list.</em>
                        )}

                        <div className="repair-modal-buttons">
                            <button className="export-button" onClick={exportRepairListToCSV}>
                                <GetAppIcon style={{ marginRight: "2px" }} />
                                Export Data
                            </button>

                            <button className="close-button" onClick={() => setRepairListModalOpen(false)}>
                                Close
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Filter Modal */}
            <Modal open={filterMenuOpen} onClose={toggleFilterMenu}>
                <Box className="filter-modal">
                    <Typography variant="h6">Select Overlays</Typography>
                    {overlays &&
                        Object.keys(overlays).map((category) => (
                            <Box key={category} className="filter-item">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={overlayVisibility[category] || false}
                                            onChange={() => toggleOverlayVisibility(category)}
                                        />
                                    }
                                    label={category}
                                />
                                <Slider
                                    min={0}
                                    max={1}
                                    step={0.05}
                                    value={overlayOpacityState[category] || 1}
                                    onChange={(e, newValue) => handleOpacityChange(category, newValue)}
                                    disabled={!overlayVisibility[category]}
                                />
                            </Box>
                        ))}
                </Box>
            </Modal>

            <Modal open={polygonFilterOpen} onClose={() => setPolygonFilterOpen(false)}>
                <Box className="filter-modal">
                    <Typography variant="h6">Grid Visibility</Typography>
                    <Slider
                        min={0}
                        max={1}
                        step={0.05}
                        value={polygonOpacity}
                        onChange={(e, newValue) => setPolygonOpacity(newValue)}
                    />
                    <Typography variant="h6">Filter by Fault Category</Typography>
                    {faultCategories.map((category) => (
                        <FormControlLabel
                            key={category}
                            control={
                                <Checkbox
                                    checked={faultVisibility[category] || false}
                                    onChange={() => toggleFaultVisibility(category)}
                                />
                            }
                            label={category}
                        />
                    ))}
                </Box>
            </Modal>

            <Modal open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
                <Box
                    sx={{
                        backgroundColor: "white",
                        padding: 3,
                        borderRadius: 2,
                        maxWidth: 400,
                        margin: "10% auto",
                        textAlign: "center",
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Pick a repair date"
                            value={tempDate}
                            onChange={(newValue) => setTempDate(newValue)}
                            disableFuture={false}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                    </LocalizationProvider>

                    <Button
                        variant="contained"
                        sx={{ mt: 2, backgroundColor: "#285335" }}
                        onClick={() => {
                            const dateString = tempDate.format("MMDDYYYY");

                            if (!repairDates.includes(dateString)) {
                                setRepairDates((prev) => [...prev, dateString]);
                            }

                            setSelectedRepairDate(dateString);
                            setDatePickerOpen(false);
                            setDropdownOpen(false);
                        }}
                    >
                        Add Date
                    </Button>
                </Box>
            </Modal>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            <Modal open={faultModalOpen} onClose={handleCloseFaultModal}>
                <Box
                    className={`fault-modal level-${(selectedFault?.level || "").toLowerCase().replace(" ", "-")}`}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1rem",
                        padding: "1rem",
                        alignItems: "flex-start",
                        maxWidth: "500px",
                    }}
                >
                    <Box sx={{ flex: "1", minWidth: "200px" }}>
                        {(() => {
                            const parsed = parseLocationString(selectedFault?.location || "");
                            if (!parsed) {
                                return (
                                    <Typography variant="h6" sx={{ marginBottom: "1rem" }}>
                                        {selectedFault?.location}
                                    </Typography>
                                );
                            }
                            return (
                                <Box
                                    sx={{
                                        marginBottom: "1rem",
                                        padding: "0.5rem",
                                        border: "1px solid #303030",
                                        borderRadius: "4px"
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        <strong>
                                            Grid Location
                                        </strong>
                                    </Typography>
                                    <Typography><strong>Inverter Block: </strong> {parsed.ib}</Typography>
                                    <Typography><strong>Group: </strong>{parsed.group}</Typography>
                                    <Typography><strong>Row: </strong>{parsed.row} North</Typography>
                                    <Typography><strong>Column: </strong>{parsed.col} West</Typography>
                                    {/* Show the original string in small text below */}
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ marginTop: "0.5rem" }}
                                    >
                                        {selectedFault?.location}
                                    </Typography>
                                </Box>
                            );
                        })()}

                        {/*<Typography variant="h6"><strong>{selectedFault?.location}</strong></Typography> */}
                        <Typography><strong>Fault Level:</strong> {selectedFault?.level}</Typography>
                        <Typography><strong>Category:</strong> {selectedFault?.category}</Typography>
                        <Typography><strong>Lat:</strong> {selectedFault?.latitude}</Typography>
                        <Typography><strong>Lng:</strong> {selectedFault?.longitude}</Typography>
                        <Typography><strong>Notes:</strong> {selectedFault?.notes}</Typography>
                    </Box>

                    {/* Display fault images */}
                    {selectedFault?.images && selectedFault.images.length > 0 ? (
                        <Box sx={{ flex: "1", maxWidth: "200px" }}>
                            {selectedFault.images.map((img, index) => (
                                <div key={index} className="image-container">
                                    <img
                                        src={img.imageUrl}
                                        alt={`Fault Image ${index + 1}`}
                                        style={{
                                            width: "100%",
                                            height: "auto",
                                            borderRadius: "8px",
                                        }}
                                    />
                                    <Typography variant="caption">
                                        Uploaded:
                                        {img.timestamp
                                            ? new Date(
                                                img.timestamp.seconds
                                                    ? img.timestamp.seconds * 1000
                                                    : img.timestamp
                                            ).toLocaleString()
                                            : "N/A"}
                                    </Typography>
                                </div>
                            ))}
                        </Box>
                    ) : (
                        <Typography> </Typography>
                    )}

                    <Button
                        variant="contained"
                        className = "add-to-repair"
                        onClick={() => handleAddPanelToRepairQueue(selectedFault)}
                    >
                        Add to Repair List
                    </Button>

                </Box>

            </Modal>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        border: "3px solid #333",
                        backgroundColor: "#fffbed",
                        padding: "16px"
                    }
                }}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove this panel from the repair list?
                    </Typography>
                    {panelToDelete && (
                        <Typography sx={{ mt: 1, fontWeight: "bold" }}>
                            {panelToDelete.location}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button
                        onClick={async () => {
                            try {
                                await deletePanelFromRepairQueue(siteName, modalRepairDate, panelToDelete);
                                const updatedPanels = await fetchPanelsForRepairDate(siteName, modalRepairDate);
                                setRepairQueue(prev => ({
                                    ...prev,
                                    [modalRepairDate]: updatedPanels,
                                }));
                                setModalRepairPanels(updatedPanels);
                            } catch (error) {
                                console.error("Failed to delete panel:", error);
                                alert("Failed to delete panel from repair list.");
                            } finally {
                                setDeleteConfirmOpen(false);
                                setPanelToDelete(null);
                            }
                        }}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>


        </div >
    );
};

export default MapPage;
